import { useState } from "react";
import {
    Box,
    Dialog,
    InputAdornment,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    useTheme,
} from "@mui/material";
import { Button, FoodsIcon, FoodsIconButton, Typography } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";
import {
    CohortDefinition,
    useDeleteCohortDefinition,
    useGetAllCohortDefinitions,
} from "src/api/client-api/cohorts/useCohortDefinitions";
import { Loading } from "src/components/utils/Loading";
import { COHORT_DEFINITION, CREATE, DELETE, MODIFY, useFoodpilotContext } from "src/context/FoodpilotContext";
import { useSnackbar } from "src/utils/useSnackbar";
import { CohortDefinitionModal } from "./CohortDefinitionModal";
import { SettingsError } from "./SettingsError";
import { useNavigateSettingsParent } from "./useNavigateSettingsParent";

const CohortsPage = () => {
    const { canUser } = useFoodpilotContext();
    const { t } = useTranslation();
    const theme = useTheme();
    const [searchTerm, setSearchTerm] = useState("");
    const { cohortDefinitions, isLoading, isError } = useGetAllCohortDefinitions();
    const deleteMutation = useDeleteCohortDefinition();
    const queryClient = useQueryClient();
    const snackbar = useSnackbar();
    const navigateSettingsParent = useNavigateSettingsParent();

    const [displayDialog, setDisplayDialog] = useState(false);
    const [idToDelete, setIdToDelete] = useState<number | undefined>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editing, setEditing] = useState<CohortDefinition | undefined>();

    const canCreate = canUser(CREATE, COHORT_DEFINITION);
    const canModify = canUser(MODIFY, COHORT_DEFINITION);
    const canDelete = canUser(DELETE, COHORT_DEFINITION);
    const hasRowActions = canModify || canDelete;

    if (isLoading) return <Loading />;

    if (isError) {
        return (
            <SettingsError
                heading={t("settings.cohorts.title", "Cohort definitions")}
                message={t(
                    "settings.cohorts.error.load_failed",
                    "Failed to load cohort definitions. Please try refreshing the page or contact support if the problem persists.",
                )}
            />
        );
    }

    const filtered = cohortDefinitions.filter((row) => {
        const term = searchTerm.toLowerCase();
        return (
            row.label.toLowerCase().includes(term) ||
            row.code.toLowerCase().includes(term) ||
            row.entityClass.toLowerCase().includes(term)
        );
    });

    const handleModalSuccess = () => {
        queryClient.invalidateQueries({ queryKey: ["getAllCohortDefinitions"] });
    };

    const confirmDelete = async () => {
        if (idToDelete === undefined) return;
        try {
            await deleteMutation.mutateAsync(idToDelete);
            queryClient.invalidateQueries({ queryKey: ["getAllCohortDefinitions"] });
            snackbar.setSnackbarProps({
                message: t("settings.cohorts.delete.success", "Cohort definition deleted"),
                type: "success",
            });
        } catch {
            snackbar.setSnackbarProps({
                message: t("settings.cohorts.delete.error", "Failed to delete cohort definition"),
                type: "error",
            });
        }
        setDisplayDialog(false);
    };

    return (
        <Box sx={{ padding: theme.spacing(3) }}>
            <Stack
                direction="row"
                alignItems="center"
                gap={theme.spacing(1)}
                marginBottom={theme.spacing(2)}
                sx={{ cursor: "pointer" }}
                onClick={navigateSettingsParent}
            >
                <FoodsIcon icon="arrowLeft" size={2} />
                <Typography variant="small">{t("common.back", "Back")}</Typography>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" marginBottom={theme.spacing(3)}>
                <Typography variant="h2">{t("settings.cohorts.title", "Cohort definitions")}</Typography>
                {canCreate && (
                    <Button
                        variant="primary"
                        startIcon={<FoodsIcon size={2} icon="plus" />}
                        onClick={() => {
                            setEditing(undefined);
                            setIsModalOpen(true);
                        }}
                    >
                        {t("settings.cohorts.add", "Add cohort")}
                    </Button>
                )}
            </Stack>

            <TextField
                fullWidth
                placeholder={t("common.search", "Search")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <FoodsIcon icon="search" size={2} />
                        </InputAdornment>
                    ),
                }}
                sx={{ marginBottom: theme.spacing(2) }}
            />

            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{t("settings.cohorts.field.code", "Code")}</TableCell>
                            <TableCell>{t("settings.cohorts.field.label", "Label")}</TableCell>
                            <TableCell>{t("settings.cohorts.field.entity_class", "Applies to")}</TableCell>
                            <TableCell>{t("settings.cohorts.field.attribute_key", "Grouping attribute")}</TableCell>
                            <TableCell align="right">
                                {t("settings.cohorts.field.minimum_peer_count", "Minimum peer count")}
                            </TableCell>
                            {hasRowActions && <TableCell align="right">{t("common.actions", "Actions")}</TableCell>}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.length === 0 ?
                            <TableRow>
                                <TableCell colSpan={hasRowActions ? 6 : 5}>
                                    <Typography variant="small-medium">
                                        {t("settings.cohorts.empty", "No cohort definitions yet.")}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        :   filtered.map((row) => (
                                <TableRow key={row.id}>
                                    <TableCell>{row.code}</TableCell>
                                    <TableCell>{row.label}</TableCell>
                                    <TableCell>{row.entityClass}</TableCell>
                                    <TableCell>{row.attributeKey}</TableCell>
                                    <TableCell align="right">{row.minimumPeerCount}</TableCell>
                                    {hasRowActions && (
                                        <TableCell align="right">
                                            <Stack direction="row" justifyContent="flex-end">
                                                {canModify && (
                                                    <FoodsIconButton
                                                        size="s"
                                                        icon="edit"
                                                        tooltip={{ text: t("common.edit", "Edit") }}
                                                        buttonProps={{
                                                            onClick: () => {
                                                                setEditing(row);
                                                                setIsModalOpen(true);
                                                            },
                                                        }}
                                                    />
                                                )}
                                                {canDelete && (
                                                    <FoodsIconButton
                                                        size="s"
                                                        icon="delete"
                                                        tooltip={{ text: t("common.delete", "Delete") }}
                                                        buttonProps={{
                                                            onClick: () => {
                                                                setIdToDelete(row.id);
                                                                setDisplayDialog(true);
                                                            },
                                                        }}
                                                    />
                                                )}
                                            </Stack>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))
                        }
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={displayDialog} onClose={() => setDisplayDialog(false)} maxWidth="xs" fullWidth>
                <Stack gap={theme.spacing(2)} sx={{ padding: theme.spacing(3) }}>
                    <Typography variant="h3">
                        {t("settings.cohorts.delete.confirm_title", "Delete cohort definition?")}
                    </Typography>
                    <Stack direction="row" gap={theme.spacing(2)} justifyContent="flex-end">
                        <Button
                            variant="secondary"
                            onClick={() => setDisplayDialog(false)}
                            disabled={deleteMutation.isLoading}
                        >
                            {t("common.cancel", "Cancel")}
                        </Button>
                        <Button variant="delete" onClick={confirmDelete} disabled={deleteMutation.isLoading}>
                            {t("common.delete", "Delete")}
                        </Button>
                    </Stack>
                </Stack>
            </Dialog>

            <CohortDefinitionModal
                open={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditing(undefined);
                }}
                definition={editing}
                onSuccess={handleModalSuccess}
            />
        </Box>
    );
};

export default CohortsPage;
